import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import { useGuruvaniFromDB } from "@jambushrushti/hooks/useContent";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Scroll,
  Search,
  BookOpen,
  Mic,
  FileText,
  Lock,
  ArrowRight,
  Quote,
  Loader2,
} from "lucide-react";

const typeIcons: Record<string, typeof Mic> = {
  Discourse: Mic,
  "Written Work": BookOpen,
  Letter: FileText,
  Notes: FileText,
};

const Guruvani = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const { data: guruvaniItems = [], isLoading } = useGuruvaniFromDB();

  const filteredItems = guruvaniItems.filter(item => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.content || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !selectedType || item.category === selectedType;
    return matchesSearch && matchesType;
  });

  const types = Array.from(
    new Set(guruvaniItems.map(item => item.category).filter(Boolean))
  );

  return (
    <Layout>
      <Seo
        title="Guruvani"
        description="Explore the profound discourses, written works, and spiritual guidance from Gurudev Muni Jambuvijayaji Maharaj Saheb - sacred teachings of Jain philosophy."
      />
      {/* Hero */}
      <section
        className="py-16 lg:py-20"
        style={{ backgroundColor: "#FAF7F2" }}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <div
              className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
              style={{
                backgroundColor: "rgba(201,162,39,0.1)",
                border: "1px solid rgba(201,162,39,0.2)",
              }}
            >
              <Scroll className="h-4 w-4" style={{ color: "#C9A227" }} />
              <span
                style={{ color: "#C9A227", fontFamily: "Inter, sans-serif" }}
              >
                Sacred Teachings
              </span>
            </div>
            <h1
              className="mb-6 text-4xl font-bold sm:text-5xl"
              style={{
                fontFamily: "Playfair Display, serif",
                color: "#2D2A26",
              }}
            >
              Guruvani
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                color: "#6B6764",
                fontSize: "1.125rem",
              }}
            >
              Explore the profound discourses, written works, letters, and
              spiritual guidance from Gurudev Muni Jambuvijayaji Maharaj.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Quote */}
      <section
        className="py-8"
        style={{
          backgroundColor: "rgba(201,162,39,0.05)",
          borderTop: "1px solid rgba(201,162,39,0.1)",
          borderBottom: "1px solid rgba(201,162,39,0.1)",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <Quote
              className="mx-auto mb-3 h-8 w-8"
              style={{ color: "rgba(201,162,39,0.3)" }}
            />
            <blockquote
              className="text-lg italic"
              style={{
                fontFamily: "Playfair Display, serif",
                color: "#2D2A26",
              }}
            >
              "The lamp of knowledge dispels the darkness of ignorance. Seek
              truth with humility and devotion."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section
        className="sticky top-16 z-40 py-8"
        style={{
          backgroundColor: "#FAF7F2",
          borderBottom: "1px solid #E8E4DD",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <div className="relative w-full flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                style={{ color: "#6B6764" }}
              />
              <Input
                placeholder="Search Guruvani..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
                style={{ borderColor: "#E8E4DD" }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setSelectedType(null)}
                style={
                  selectedType === null
                    ? { backgroundColor: "#C9A227", color: "white" }
                    : {
                        backgroundColor: "white",
                        color: "#2D2A26",
                        border: "1px solid #E8E4DD",
                      }
                }
              >
                All
              </Button>
              {types.map(type => (
                <Button
                  key={type}
                  size="sm"
                  onClick={() => setSelectedType(type || null)}
                  style={
                    selectedType === type
                      ? { backgroundColor: "#C9A227", color: "white" }
                      : {
                          backgroundColor: "white",
                          color: "#2D2A26",
                          border: "1px solid #E8E4DD",
                        }
                  }
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Items Grid */}
      <section className="py-12" style={{ backgroundColor: "#FAF7F2" }}>
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin"
                style={{ color: "#C9A227" }}
              />
            </div>
          ) : (
            <>
              {/* Access Notice */}
              <div
                className="mb-8 flex items-start gap-4 rounded-xl p-4"
                style={{
                  backgroundColor: "rgba(201,162,39,0.05)",
                  border: "1px solid #E8E4DD",
                }}
              >
                <Lock
                  className="mt-0.5 h-5 w-5 shrink-0"
                  style={{ color: "#6B6764" }}
                />
                <div>
                  <p
                    className="mb-1 font-medium"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      color: "#2D2A26",
                    }}
                  >
                    Some content requires authentication
                  </p>
                  <p
                    style={{
                      fontFamily: "Inter, sans-serif",
                      color: "#6B6764",
                    }}
                  >
                    Items marked with a lock icon require you to be logged in.
                    Scholars may request access to restricted materials.
                  </p>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredItems.map((item, index) => {
                  const Icon = typeIcons[item.category || ""] || FileText;
                  const isRestricted = item.is_restricted;
                  return (
                    <Link
                      key={item.id}
                      to={isRestricted ? "/auth" : `/guruvani/${item.id}`}
                      className="group"
                    >
                      <div
                        className="h-full overflow-hidden rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                        style={{
                          backgroundColor: "white",
                          border: "1px solid #E8E4DD",
                        }}
                      >
                        {/* Cover Image */}
                        <div
                          className="relative aspect-[4/3] overflow-hidden"
                          style={{ backgroundColor: "#F5EFE6" }}
                        >
                          <img
                            src={
                              item.image_url ||
                              "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
                            }
                            alt={item.title}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute left-3 right-3 top-3 flex items-start justify-between">
                            <Badge
                              className="border-0 text-xs"
                              style={{
                                backgroundColor: "rgba(255,255,255,0.9)",
                                color: "#2D2A26",
                              }}
                            >
                              <Icon className="mr-1 h-3 w-3" />
                              {item.category || "Discourse"}
                            </Badge>
                            {isRestricted && (
                              <Badge
                                className="text-xs"
                                style={{
                                  backgroundColor: "rgba(255,255,255,0.9)",
                                  color: "#6B6764",
                                }}
                              >
                                <Lock className="mr-1 h-3 w-3" />
                                Restricted
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="mb-2 flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs"
                              style={{
                                borderColor: "#E8E4DD",
                                color: "#6B6764",
                              }}
                            >
                              {item.source || "Gujarati"}
                            </Badge>
                            <span
                              className="text-xs"
                              style={{ color: "#6B6764" }}
                            >
                              {item.date
                                ? new Date(item.date).getFullYear()
                                : ""}
                            </span>
                          </div>
                          <h3
                            className="mb-2 line-clamp-2 text-lg font-semibold transition-opacity group-hover:opacity-80"
                            style={{
                              fontFamily: "Playfair Display, serif",
                              color: "#2D2A26",
                            }}
                          >
                            {item.title}
                          </h3>
                          <p
                            className="mb-4 line-clamp-2 text-sm"
                            style={{
                              fontFamily: "Inter, sans-serif",
                              color: "#6B6764",
                            }}
                          >
                            {item.content}
                          </p>
                          <div
                            className="pt-3"
                            style={{ borderTop: "1px solid #E8E4DD" }}
                          >
                            {isRestricted ? (
                              <div
                                className="flex items-center justify-center gap-2 text-sm"
                                style={{ color: "#6B6764" }}
                              >
                                <Lock className="h-3.5 w-3.5" />
                                <span>Sign In to Access</span>
                              </div>
                            ) : (
                              <div
                                className="flex items-center justify-center text-sm font-medium group-hover:underline"
                                style={{ color: "#C9A227" }}
                              >
                                Read More
                                <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {filteredItems.length === 0 && (
                <div className="py-12 text-center">
                  <p style={{ color: "#6B6764" }}>
                    No items found matching your search.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Request Access CTA */}
      <section className="py-16" style={{ backgroundColor: "#F5EFE6" }}>
        <div className="container mx-auto px-4 text-center">
          <h2
            className="mb-4 text-2xl font-bold"
            style={{ fontFamily: "Playfair Display, serif", color: "#2D2A26" }}
          >
            Access Restricted Teachings
          </h2>
          <p
            className="mx-auto mb-8 max-w-lg"
            style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
          >
            Scholars and dedicated seekers can request access to restricted
            Guruvani materials. Create an account and apply through the Scholar
            Portal.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              asChild
              style={{ backgroundColor: "#C9A227", color: "white" }}
            >
              <Link to="/auth">Create Account</Link>
            </Button>
            <Button
              variant="outline"
              asChild
              style={{ borderColor: "#E8E4DD", color: "#2D2A26" }}
            >
              <Link to="/scholars">Scholar Portal</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Guruvani;
